import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/99fb78cdc497b8940e1fbad9779f44095e5b1eac7adfb04221605f52971d01a3.png";
const ESCALADE_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/36e99a6c68af7aa56e11f20fa0ba9bb87ff0e136fd1009eb4f06558841de308b.png";
const MERCEDES_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/2b67039dea220f49085a0ea0e70669c526bf6f66f0f4ac4148612b2ba893f9fe.png";
const SUBURBAN_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/7ac0ae0540720f74219081dbd736edcab8aa0376e716dea85a6290e701face64.png";

const useFadeObserver = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.08, rootMargin: '0px 0px -80px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

export const ChauffeurHub = () => {
  const navigate = useNavigate();
  useFadeObserver();

  const audiences = [
    'Executives and founders',
    'Corporate travel and meetings',
    'Airport arrivals and departures',
    'Families and private groups',
    'Special events and evenings out',
  ];

  const standards = [
    'Professional, vetted chauffeurs',
    'Premium SUVs with immaculate interiors',
    'On-time, structured, confirmation-based approach',
    'Discreet, respectful, hospitality-driven service',
    'Real-time communication and transparent coordination',
  ];

  const steps = [
    { num: '01', title: 'Submit Your Request', desc: 'Share your pickup details, timing, and preferences.' },
    { num: '02', title: 'Receive Confirmation', desc: 'We confirm availability and send your quote.' },
    { num: '03', title: 'Your Chauffeur Arrives', desc: 'On time, prepared, and aligned with your itinerary.' },
  ];

  const fleet = [
    { name: 'Cadillac Escalade ESV', image: ESCALADE_IMG },
    { name: 'Mercedes-Benz S-Class', image: MERCEDES_IMG },
    { name: 'Chevrolet Suburban Premier', image: SUBURBAN_IMG },
  ];

  return (
    <div data-testid="chauffeur-hub-page" style={{ backgroundColor: '#0D0D0D' }}>

      {/* HERO */}
      <section data-testid="chauffeur-hero" className="relative overflow-hidden flex items-center" style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.48) saturate(0.95) contrast(1.05)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(90deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.1) 70%, rgba(0,0,0,0.15) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.6) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 20% 50%, rgba(194,168,120,0.04) 0%, transparent 50%)',
        }} />

        <div className="relative z-10" style={{ paddingLeft: '56px', paddingRight: '48px', paddingTop: '180px', paddingBottom: '160px', maxWidth: '680px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s', marginBottom: '28px' }}>
            <div className="gold-divider" style={{ marginBottom: '20px' }} />
            <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.65)', letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 300 }}>
              Toronto Royale Chauffeur
            </p>
          </div>

          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="chauffeur-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(44px, 5.5vw, 68px)', fontWeight: 500, color: '#FAFAFA', lineHeight: 1.08, marginBottom: '24px',
              textShadow: '0 2px 40px rgba(0,0,0,0.4)',
            }}>
              Private Chauffeur Service by Toronto Royale
            </h1>
          </div>

          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '18px', fontWeight: 300, color: 'rgba(220,220,220,0.75)', lineHeight: 1.7, marginBottom: '16px', maxWidth: '520px' }}>
              Discreet, on-time, luxury SUV transport for airport, corporate, and private travel.
            </p>
            <p style={{ fontSize: '13px', fontWeight: 300, color: 'rgba(194,168,120,0.45)', letterSpacing: '0.5px', marginBottom: '40px' }}>
              Serving the Greater Toronto Area
            </p>
          </div>

          <div className="fade-element flex flex-wrap items-center" style={{ gap: '16px', animationDelay: '0.8s' }}>
            <button
              data-testid="chauffeur-hero-cta"
              onClick={() => navigate('/chauffeur/book')}
              style={{
                backgroundColor: '#C2A878', color: '#0A0A0A', padding: '15px 36px',
                fontSize: '12px', fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase',
                border: 'none', cursor: 'pointer', transition: 'all 400ms ease',
                boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
            >
              Request a Chauffeur
            </button>
            <button
              data-testid="chauffeur-hero-secondary-cta"
              onClick={() => navigate('/chauffeur/fleet')}
              style={{
                backgroundColor: 'transparent', color: '#C2A878', padding: '15px 36px',
                fontSize: '12px', fontWeight: 400, letterSpacing: '1.2px', textTransform: 'uppercase',
                border: '1px solid rgba(194,168,120,0.3)', cursor: 'pointer', transition: 'all 400ms ease',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(194,168,120,0.08)'; e.target.style.borderColor = 'rgba(194,168,120,0.5)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(194,168,120,0.3)'; }}
            >
              View Our Fleet
            </button>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0" style={{ height: '140px', background: 'linear-gradient(to top, #0D0D0D, transparent)' }} />

        {/* Hero zoom keyframe */}
        <style>{`
          @keyframes heroZoom {
            0% { transform: scale(1.05); }
            100% { transform: scale(1.12); }
          }
        `}</style>
      </section>

      {/* DESIGNED FOR THOSE WHO MOVE WITH INTENTION */}
      <section data-testid="chauffeur-audience" className="relative overflow-hidden section-depth" style={{ backgroundColor: '#0E0E0E', padding: '10rem 1.5rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 20% 80%, rgba(194,168,120,0.04) 0%, transparent 50%)',
        }} />
        <div className="grain-overlay absolute inset-0" />

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center" style={{ gap: '80px' }}>
            <div className="fade-element">
              <div className="gold-divider" style={{ marginBottom: '24px' }} />
              <h2 className="font-serif" style={{ fontSize: 'clamp(30px, 3.5vw, 44px)', fontWeight: 500, color: '#F5F5F5', lineHeight: 1.2, marginBottom: '24px' }}>
                Designed for Those Who Move with Intention
              </h2>
              <p style={{ fontSize: '16px', color: 'rgba(225,225,225,0.85)', lineHeight: 1.8, fontWeight: 300 }}>
                Toronto Royale Chauffeur is built for individuals and teams who value precision, professionalism, and a seamless travel experience.
              </p>
            </div>
            <div className="fade-element">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {audiences.map((item, i) => (
                  <div key={i} className="flex items-center" style={{ gap: '16px' }}>
                    <div style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(194,168,120,0.06)', borderRadius: '2px', flexShrink: 0 }}>
                      <Check size={13} style={{ color: '#C2A878', opacity: 0.7 }} />
                    </div>
                    <span style={{ fontSize: '15px', color: 'rgba(204,204,204,0.7)', fontWeight: 300 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A HIGHER STANDARD */}
      <section data-testid="chauffeur-standard" className="relative overflow-hidden section-depth" style={{ backgroundColor: '#0D0D0D', padding: '10rem 1.5rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 80% 30%, rgba(194,168,120,0.03) 0%, transparent 60%)',
        }} />

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center fade-element" style={{ marginBottom: '72px' }}>
            <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.55)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '16px' }}>
              Our Standard
            </p>
            <h2 className="font-serif" style={{ fontSize: 'clamp(30px, 3.5vw, 44px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '24px' }}>
              A Higher Standard of Chauffeur Service
            </h2>
            <div className="gold-divider mx-auto" style={{ marginBottom: '24px' }} />
            <p style={{ fontSize: '16px', color: 'rgba(204,204,204,0.55)', maxWidth: '560px', margin: '0 auto', fontWeight: 300, lineHeight: 1.7 }}>
              Every journey is delivered with the same intentionality that defines the Toronto Royale brand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: '40px' }}>
            {standards.map((item, i) => (
              <div key={i} className="fade-element" style={{
                padding: '32px',
                backgroundColor: 'rgba(26,26,26,0.3)',
                border: '1px solid rgba(194,168,120,0.07)',
                transition: 'border-color 600ms ease',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(194,168,120,0.16)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(194,168,120,0.07)'; }}
              >
                <div style={{ width: '1px', height: '24px', background: 'rgba(194,168,120,0.3)', marginBottom: '20px' }} />
                <p style={{ fontSize: '15px', color: 'rgba(204,204,204,0.65)', fontWeight: 300, lineHeight: 1.7 }}>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A SIMPLE, STRUCTURED PROCESS */}
      <section data-testid="chauffeur-process" className="relative overflow-hidden section-depth" style={{ backgroundColor: '#111111', padding: '10rem 1.5rem' }}>
        <div className="grain-overlay absolute inset-0" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center fade-element" style={{ marginBottom: '72px' }}>
            <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.55)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '16px' }}>
              How It Works
            </p>
            <h2 className="font-serif" style={{ fontSize: 'clamp(30px, 3.5vw, 44px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '16px' }}>
              A Simple, Structured Process
            </h2>
            <div className="gold-divider mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: '56px' }}>
            {steps.map((step, i) => (
              <div key={i} className="text-center fade-element">
                <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.4)', letterSpacing: '3px', fontWeight: 300, marginBottom: '20px' }}>{step.num}</p>
                <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(194,168,120,0.15)', margin: '0 auto 20px' }} />
                <h3 className="font-serif" style={{ fontSize: '20px', fontWeight: 400, color: '#F5F5F5', marginBottom: '14px' }}>{step.title}</h3>
                <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', fontWeight: 300, lineHeight: 1.7 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR PREMIUM SUV FLEET */}
      <section data-testid="chauffeur-fleet-preview" className="relative overflow-hidden section-depth" style={{ backgroundColor: '#0D0D0D', padding: '10rem 1.5rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.03) 0%, transparent 60%)',
        }} />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center fade-element" style={{ marginBottom: '72px' }}>
            <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.55)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '16px' }}>
              The Fleet
            </p>
            <h2 className="font-serif" style={{ fontSize: 'clamp(30px, 3.5vw, 44px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '24px' }}>
              Our Premium SUV Fleet
            </h2>
            <div className="gold-divider mx-auto" style={{ marginBottom: '24px' }} />
            <p style={{ fontSize: '16px', color: 'rgba(204,204,204,0.55)', maxWidth: '560px', margin: '0 auto', fontWeight: 300, lineHeight: 1.7 }}>
              A curated selection of luxury SUVs designed for comfort, safety, and presence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: '32px', marginBottom: '56px' }}>
            {fleet.map((vehicle, i) => (
              <div key={i} className="fade-element group">
                <div className="overflow-hidden relative" style={{ marginBottom: '20px' }}>
                  <div
                    className="bg-cover bg-center transition-transform duration-1000 group-hover:scale-105"
                    style={{
                      height: '260px',
                      backgroundImage: `url(${vehicle.image})`,
                      filter: 'brightness(0.65) saturate(0.9)',
                    }}
                  />
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(180deg, transparent 50%, rgba(13,13,13,0.5) 100%)',
                  }} />
                </div>
                <h3 className="font-serif text-center" style={{ fontSize: '18px', fontWeight: 400, color: '#F5F5F5' }}>{vehicle.name}</h3>
              </div>
            ))}
          </div>

          <div className="text-center fade-element">
            <button
              data-testid="view-fleet-btn"
              onClick={() => navigate('/chauffeur/fleet')}
              className="inline-flex items-center"
              style={{
                gap: '10px', backgroundColor: 'transparent', color: '#C2A878',
                padding: '14px 32px', fontSize: '13px', fontWeight: 400, letterSpacing: '0.8px',
                textTransform: 'uppercase', border: '1px solid rgba(194,168,120,0.3)',
                cursor: 'pointer', transition: 'all 400ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(194,168,120,0.08)'; e.currentTarget.style.borderColor = 'rgba(194,168,120,0.5)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(194,168,120,0.3)'; }}
            >
              View Full Fleet <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* SERVING TORONTO AND THE GTA */}
      <section data-testid="chauffeur-service-areas" className="relative overflow-hidden section-depth" style={{ backgroundColor: '#111111', padding: '8rem 1.5rem' }}>
        <div className="grain-overlay absolute inset-0" />
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1760662544434-dd8d479bfc83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxUb3JvbnRvJTIwc2t5bGluZSUyMG5pZ2h0JTIwY2luZW1hdGljJTIwZGFyayUyMGNpdHlzY2FwZXxlbnwwfHx8fDE3NzI1NjE3Nzd8MA&ixlib=rb-4.1.0&q=85)`,
          filter: 'brightness(0.20) saturate(0.6)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(17,17,17,0.95) 0%, rgba(17,17,17,0.6) 50%, rgba(17,17,17,0.95) 100%)',
        }} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="fade-element">
            <div className="gold-divider mx-auto" style={{ marginBottom: '24px' }} />
            <h2 className="font-serif" style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '28px' }}>
              Serving Toronto and the Greater Toronto Area
            </h2>
            <p style={{ fontSize: '16px', color: 'rgba(204,204,204,0.55)', fontWeight: 300, lineHeight: 1.8, maxWidth: '640px', margin: '0 auto' }}>
              Pearson Airport, Billy Bishop Airport, Downtown Toronto, Mississauga, Oakville, Milton, Vaughan, Markham, and extended routes including Niagara and Waterloo.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="chauffeur-final-cta" className="relative overflow-hidden section-depth" style={{ backgroundColor: '#0D0D0D', padding: '10rem 1.5rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.04) 0%, transparent 60%)',
        }} />
        <div className="max-w-3xl mx-auto text-center fade-element relative z-10">
          <div className="gold-divider mx-auto" style={{ marginBottom: '28px' }} />
          <h2 className="font-serif" style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '20px' }}>
            Request Your Chauffeur
          </h2>
          <p style={{ fontSize: '17px', color: 'rgba(225,225,225,0.8)', fontWeight: 300, marginBottom: '48px', lineHeight: 1.6 }}>
            Submit your details and we'll confirm availability shortly.
          </p>
          <button
            data-testid="chauffeur-final-cta-btn"
            onClick={() => navigate('/chauffeur/book')}
            style={{
              backgroundColor: '#C2A878', color: '#0B0B0B', padding: '14px 36px',
              fontSize: '13px', fontWeight: 400, letterSpacing: '0.8px', textTransform: 'uppercase',
              border: 'none', cursor: 'pointer', transition: 'background-color 300ms ease',
            }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#BFA77A'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; }}
          >
            Request a Chauffeur
          </button>
        </div>
      </section>
    </div>
  );
};
